import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Window } from '../../ui/Window';
import signatureImage from '../../../assets/signature.png';
import { useGameStore } from '../../../stores/gameStore';
import { playSound } from '../../../utils/soundService';

interface NdaAgreementWindowProps {
  onClose: () => void;
}

export function NdaAgreementWindow({ onClose }: NdaAgreementWindowProps) {
  const { ndaSigned, setNdaSigned } = useGameStore();
  const [printedName, setPrintedName] = useState('');
  const [showMarkBadge, setShowMarkBadge] = useState(false);
  const [badgePosition, setBadgePosition] = useState({ top: 0, left: 0 });
  const contentRef = useRef<HTMLDivElement>(null);
  const markSpanRef = useRef<HTMLSpanElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMarkBadge) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        badgeRef.current &&
        !badgeRef.current.contains(e.target as Node) &&
        markSpanRef.current &&
        !markSpanRef.current.contains(e.target as Node)
      ) {
        setShowMarkBadge(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMarkBadge]);

  return (
    <Window
      title={`${ndaSigned ? 'NDA Agreement (Signed)' : 'NDA Agreement'}`}
      onClose={onClose}
      width={800}
      height={600}
    >
      <div ref={contentRef} className="overflow-y-auto p-6">
        <div className="text-sm leading-relaxed text-gray-700 space-y-4">
          <h2 className="text-base font-semibold text-gray-900">
            NON-DISCLOSURE AGREEMENT (Full Document)
          </h2>

          <p>
            This comprehensive Non-Disclosure Agreement (&quot;NDA&quot;)
            establishes the legal framework governing the protection of
            confidential information shared between Laura.ai and its employees.
            This document serves as the binding agreement referenced in your
            employment contract and must be acknowledged to maintain your access
            to company systems and confidential information.
          </p>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">Definitions</h3>
            <p>
              &quot;Confidential Information&quot; includes all proprietary
              data, secrets, business plans, financial information, technical
              specifications, source code, client lists, marketing strategies,
              and any other information that would reasonably be considered
              sensitive or proprietary in nature.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">
              Restrictions on Use
            </h3>
            <p>
              The receiving party agrees not to use Confidential Information for
              any purpose other than performing job duties. Unauthorized
              disclosure, copying, or reproduction of Confidential Information
              is strictly prohibited and may result in legal action.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">
              Duration and Survival
            </h3>
            <p>
              This agreement survives termination of employment for three (3)
              years. Certain provisions regarding trade secrets may survive
              indefinitely as permitted by law.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 mb-2">Remedies</h3>
            <p>
              In the event of breach, the company shall be entitled to seek
              injunctive relief, monetary damages, and any other remedies
              available at law or in equity. The employee acknowledges that
              breach may result in immediate termination and legal proceedings.
            </p>
          </section>

          <section>
            <p className="font-bold text-gray-900 my-4">
              ON-CALL DEVELOPERS: Signing this NDA is mandatory to use internal
              tools including the Laura Monitor Center.
            </p>
          </section>

          <section className="bg-gray-100 border border-gray-300 p-4 rounded relative">
            <div className="relative inline-block">
              <p className="font-bold text-gray-900">
                ⚠ WARNING: Signing this NDA is required for badge activation.
              </p>
            </div>
            <div className="absolute top-10 right-1 bg-yellow-100 border border-yellow-400 rounded p-3 w-58 z-10 shadow-lg">
              <div>
                <p>
                  please understand, this is common practice. <br /> -{' '}
                  <span
                    ref={markSpanRef}
                    onClick={() => {
                      const rect = markSpanRef.current?.getBoundingClientRect();
                      if (rect) {
                        setBadgePosition({
                          top: rect.top - 285,
                          left: rect.left,
                        });
                      }
                      setShowMarkBadge(!showMarkBadge);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        const rect =
                          markSpanRef.current?.getBoundingClientRect();
                        if (rect) {
                          setBadgePosition({
                            top: rect.top - 185,
                            left: rect.left,
                          });
                        }
                        setShowMarkBadge(!showMarkBadge);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer underline hover:text-blue-600"
                  >
                    Mark
                  </span>
                </p>
              </div>
            </div>
          </section>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-lg font-semibold text-gray-900 mb-4">
              EMPLOYEE ACKNOWLEDGMENT
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-md text-gray-600 mb-1">
                  Printed Name (Must match name in badge)
                </p>
                <input
                  type="text"
                  disabled={ndaSigned}
                  value={ndaSigned ? 'Trent Tres' : printedName}
                  onChange={(e) => {
                    setPrintedName(e.target.value);
                    if (e.target.value === 'Trent Tres') {
                      playSound('signature');
                      setNdaSigned(true);
                    }
                  }}
                  className="w-full h-8 px-0 py-1 border-b border-gray-400 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-600 bg-transparent"
                />
              </div>
              <div>
                <p className="text-md text-gray-600 mb-1">Signature</p>
                <div className="border-b border-gray-400 h-8 flex items-center overflow-hidden">
                  {printedName === 'Trent Tres' && (
                    <motion.img
                      src={signatureImage}
                      alt="Signature"
                      className="h-40 object-cover"
                      initial={{ clipPath: 'inset(0 100% 0 0)' }}
                      animate={{ clipPath: 'inset(0 0% 0 0)' }}
                      transition={{ duration: 1.5, ease: 'easeOut' }}
                    />
                  )}
                  {ndaSigned && printedName !== 'Trent Tres' && (
                    <img
                      src={signatureImage}
                      alt="Signature"
                      className="h-40 object-cover"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 mt-20">
            <p className="text-xs text-gray-500">
              Document ID: NDA-2025-001-FULL | Confidential - For Authorized
              Personnel Only
            </p>
          </div>
        </div>

        {/* Mark's Badge Popup */}
        {showMarkBadge && (
          <div
            ref={badgeRef}
            className="fixed bg-white border border-gray-300 rounded shadow-lg p-4 w-48"
            style={{
              top: `${badgePosition.top}px`,
              left: `${badgePosition.left}px`,
              zIndex: 1000,
            }}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="text-center text-sm font-semibold text-gray-900">
                laura.ai
              </div>
              <div className="w-32 h-40 border border-gray-300 bg-gray-100 flex items-center justify-center">
                <div
                  className="w-full h-full"
                  style={{
                    backgroundImage: `
                      linear-gradient(135deg, #3a3a3a 25%, transparent 25%),
                      linear-gradient(225deg, #3a3a3a 25%, transparent 25%),
                      linear-gradient(45deg, #3a3a3a 25%, transparent 25%),
                      linear-gradient(315deg, #3a3a3a 25%, #e5e5f7 25%)
                    `,
                    backgroundSize: '20px 20px',
                    backgroundRepeat: 'repeat',
                  }}
                />
              </div>
              <div className="text-center text-xs font-semibold text-gray-900">
                Mark Zhang
              </div>
              <div className="text-center text-xs font-semibold text-gray-900">
                mark.2
              </div>
            </div>
          </div>
        )}
      </div>
    </Window>
  );
}
